import React from 'react';
import type { ViewProps } from '../View/View';
import View from '../View/View';
import Text from '../Text';
import Link from 'next/link';
import Box from '../Box/Box';
import type { BoxProps } from '@mantine/core';
import type { InputProps } from '../Input/Input';
import styled from 'styled-components';

export type MenuProps = {
  children: React.ReactNode;
  hasShadow?: boolean;
} & ViewProps;

const Menu = ({
  children,
  hasShadow = false,
  ...restProps
}: MenuProps): JSX.Element => {
  const shadowStyles = {
    boxShadow: '0px 2px 6.62px rgba(0, 0, 0, 0.33)',
  };

  return (
    <View
      minWidth={200}
      bg="dropdownList.background"
      p={2}
      borderColor="dropdownList.borderColor"
      borderWidth="1px"
      style={hasShadow ? shadowStyles : {}}
      {...restProps}
    >
      {children}
    </View>
  );
};

export interface MenuItemProps extends BoxProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  selected?: boolean;
  /** True/false will display a check box next to the label */
  checked?: boolean;
  readOnly?: boolean;
  /**
   * Overrides the text that's read by the screen reader when the user interacts with the element. By default, the
   * label is constructed by traversing all the children and accumulating all the Text nodes separated by space.
   */
  accessibilityLabel?: string | undefined;
}

const MenuItemBg = styled(View)<{ disabled?: boolean; selected?: boolean }>`
  background-color: ${(props) =>
    props.disabled
      ? 'transparent'
      : props.selected
        ? props.theme.colors.neutral.gray_155
        : props.theme.colors.neutral.white_100};
  cursor: ${(props) => (props.disabled ? 'initial' : 'pointer')};

  &:hover {
    background-color: ${(props) =>
      props.disabled ? 'transparent' : props.theme.colors.neutral.gray_155};
  }
`;

export const MenuItem = ({
  label,
  onPress,
  disabled,
  checked,
  readOnly,
  selected,
  accessibilityLabel,
  ...restProps
}: MenuItemProps): JSX.Element => {
  function onClick() {
    if (disabled) return;
    onPress();
  }

  return (
    <MenuItemBg
      role="button"
      disabled={disabled}
      selected={selected}
      p={3}
      onClick={onClick}
      aria-label={accessibilityLabel || label}
      flexDirection="row"
      {...restProps}
    >
      <Text
        fontWeight={'bold'}
        color={disabled ? 'neutral.gray_225' : 'neutral.black_900'}
        isUnselectable
      >
        {label}
      </Text>
      {checked !== undefined && (
        <Box
          as="input"
          type="checkbox"
          checked={checked}
          readOnly={readOnly}
          marginLeft={2}
          fontSize="1.2em"
        />
      )}
    </MenuItemBg>
  );
};

export interface MenuLinkProps extends ViewProps {
  label: string;
  href: string;
}

export const MenuLink = ({ href, label }: MenuLinkProps): JSX.Element => {
  return (
    <Link href={href} style={{ textDecoration: 'none' }}>
      <MenuItemBg p={3}>
        <Text color={'neutral.black_900'} isUnselectable>
          {label}
        </Text>
      </MenuItemBg>
    </Link>
  );
};

export const MenuSeparator = (): JSX.Element => {
  return <View width={1} height={1} bg="neutral.gray_150" my={2} />;
};

export function MenuToggle({
  label,
  checked,
  onChange,
  disabled,
  type,
  title,
  width,
  height,
  ...boxProps
}: {
  label: string;
  type: 'checkbox' | 'radio';
  title?: string;
  width?: number | string;
  height?: number | string;
} & BoxProps &
  Pick<InputProps, 'checked' | 'onChange' | 'disabled'>): JSX.Element {
  return (
    <Box as="label" title={title} {...boxProps}>
      <Box
        as="input"
        type={type}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        verticalAlign="middle"
        mr={2}
        width={width}
        height={height}
      />
      <Text isUnselectable verticalAlign="middle">
        {label}
      </Text>
    </Box>
  );
}

export default Menu;
